/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/*
Add only those constants whose values are meaningful.
Eg: MIN_ZOOM = 0.1 //its value is important for setting initial default minimum zoom level.
Dont' declare enums here, Have a look at enums.js to find out right place to put constants.
*/
export const DEFAULT_ZOOM_LEVEL = 1;
export const DEFAULT_MIN_ZOOM = 0.01;
export const DEFAULT_MAX_ZOOM = 16;
export const ZOOM_FACTOR = 96 / 72; // pdf defines length of 1 dot = 1/72 inch; considering 1 dot length on a physical device = 1/96 inch
export const BITS_PER_PIXEL = 24;
export const MAX_IMAGE_SIZE = 8 * 1024 * 8 * 1024 * 3; // Images greater than this resolution (192 MB) are not supported
export const ACROFORM_CLIENT_NAME = "acroform";
export const ACROFORM_DROPIN_NAME = "acroform";
export const FILLSIGN_CLIENT_NAME = "fillsign";

export const THUMBNAIL_WIDTH = 112;
export const THUMBNAIL_HEIGHT = 140;
export const THUMBNAIL_WIDTH_MODERN_VIEWER = 114;
export const THUMBNAIL_HEIGHT_MODERN_VIEWER = 150;
export const THUMBNAIL_OFFSET_SIZE = 10;

export const SCRUBBER_HEIGHT = 28;
export const SCRUBBER_PAGE_NUM_INDICATOR_HEIGHT = 106;

export const HUD_HEIGHT = 48;
export const HUD_ACTIVATION_REGION = 200;
export const HUD_HIDE_TIMEOUT = 3000;
export const HUD_ANIMATION_IN_DURATION = 250;
export const HUD_ANIMATION_OUT_DURATION = 2000;
export const FILENAME_TOAST_HIDE_TIMEOUT = 5000;
export const FLYOUT_HIDE_TIMEOUT = 4000;
export const QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT = 1000;
export const QUICK_TOOLS_FLYOUT_HIDE_TIMEOUT_FOR_TOUCH_DEVICES = 3000;
export const DISABLED_QT_TOAST_TIMEOUT = 5000;
export const FILENAME_TOAST_MAX_WIDTH = 310;
export const FILENAME_TOAST_BORDER_WIDTH = 1;
export const FILENAME_TOAST_ACTIVATION_REGION = 0.2;
export const HUD_BOTTOM_MARGIN = 30;
export const HUD_BOTTOM_MARGIN_MODERNVIEWER = 24;
export const HUD_OVERFLOW_CONTEXTMENU_SIZED_MARGINRIGHT = 16;
export const HUD_OVERFLOW_CONTEXTMENU_INLINE_MARGINRIGHT = 0;
export const HUD_OVERFLOW_CONTEXTMENU_SIZED_MARGINBOTTOM = 16;
export const HUD_OVERFLOW_CONTEXTMENU_INLINE_MARGINBOTTOM = 8;

export const HUD_PROGRESS_BAR_OPACITY = 0.8;

export const PROGRESS_BAR_HEIGHT = 5;
export const PROGRESS_BAR_SCRUBBER_HEIGHT = 18;
export const PROGRESS_BAR_TOOLTIP_RIGHT_MARGIN = 5;
export const PROGRESS_BAR_BACKGROUND_COLOR = "#a2a2a2";
export const PROGRESS_BAR_ACTIVE_COLOR = "#5aa9fa";

export const MAX_PAGES_CACHE_MEMORY = 100 * 1024 * 1024; // 25megs
export const MAX_THUMBNAILS_CACHE_MEMORY = 4 * 1024 * 1024; // 4 megs
export const MAX_PAGES_CACHE_WINDOW = 10;
export const THUMBNAILS_CACHE_WINDOW = 10;
export const CACHE_WINDOW_FOR_ZOOMLEVEL_1 = 8;
export const ACCESSIBILITY_CACHING_WINDOW = 20;
export const YOUTUBE_LINKS_CNT_PAGES_THRESHOLD = 10;

export const ADOBE_DOCUMENT_CLOUD = "Adobe cloud storage";

// Priorities
export const PAGE_PRIORITY = 10;
export const PAGE_WITH_MISSINGTEXT_PRIORITY = 5; // missing text page priority is greater than that of page priority.
export const GET_PAGE_GEOMETRY_PRIORITY = 30;
export const THUMBNAIL_PRIORITY = 20;
export const READALOUD_TEXT_EXTRACT_PRIORITY = 14;
export const API_TEXT_EXTRACT_PRIORITY = 14;
export const ACCESSIBILITY_TEXT_EXTRACT_PRIORITY = 16;
export const ACCESSIBILITY_CACHE_PRIORITY = 30;
export const PAGE_CACHE_PRIORITY = 40;
export const THUMBNAIL_CACHE_PRIORITY = 50;
export const SEARCH_HIGH_PRIORITY = 13;
export const SEARCH_LOW_PRIORITY = 35;
export const COPY_TEXT_PRIORITY = 20;
export const GET_COMMENTS_LIST_PRIORITY = 15;
export const GET_ACROFORM_LIST_PRIORITY = 15;
export const GET_ACROFORM_APPEARANCE_PRIORITY = 15;
export const GET_PAGE_FONT_LIST_PRIORITY = 20;
export const GET_PAGES_SCAN_STATUS_PRIORITY = 5;
export const GET_IS_SCANNED_DOCUMENT = 5;
export const GET_DATA_FROM_NAMES_TREE_PRIORITY = 51;
export const GET_HYPERLINK_LIST_PRIORITY = 15;
export const HYPERLINK_RENDERING_PRIORITY = 12;
export const FORCE_FETCH_PAGE_GEOMETRY_PRIORITY = 6;
export const CURSOR_RENDITION_PRIORITY = 10;
export const GET_HYPERLINK_LIST_FOR_CNT_PRIORITY = 30;

export const PAGE_BOTTOM_MARGIN = 15;
export const TWO_COLUMN_MARGIN = 4;
export const CURR_PAGE_TOP_OFFSET_PERCENT = 40;
export const PAGE_GEOMETRY_BATCH_SIZE = 10;
export const HIGH_PRIORITY = 22;
export const LOW_PRIORITY = 23;
export const RANDOM_SCOPE_STRING_LENGTH = 72;
export const PAGE_TOPBAR_HEIGHT = 64;
export const PAGES_TOP_PADDING = 0;

export const THUMBNAILS_TOP_PADDING = 20;
export const THUMBNAILS_TOP_PADDING_MODERN_VIEWER = 0;
export const THUMBNAILS_TOP_PADDING_NEW_GRID = 20;
export const THUMBNAILS_MIN_SPACE_BETWEEN_NEW_GRID = 25;
export const LHP_ACTION_PANEL_WIDTH = 48;
export const LHP_ACTION_PANEL_BORDER = 1;
export const THUMBNAILS_FEATURE_WIDTH = 250;
export const THUMBNAILS_FEATURE_WIDTH_MODERN_VIEWER = 304;
export const THUMBNAILS_PAGES_HEADER_HEIGHT_MODERN_VIEWER = 64;
export const THUMBNAILS_PAGES_HEADER_HEIGHT_CLASSIC_VIEWER = 0;
export const BOOKMARKS_FEATURE_WIDTH = 250;
export const ATTACHMENTS_FEATURE_WIDTH = 250;

export const CONTEXT_BOARD_WIDTH = 296;

export const PAGES_VIEW_MARGIN_LEFT = 66;
export const PAGES_VIEW_MARGIN_RIGHT = 0;

export const PAGE_MARGIN_BOTTOM = 15;
export const THUMBNAIL_MARGIN = 2;
export const THUMBNAIL_MARGIN_MODERN_VIEWER = 0;
export const THUMBNAIL_PADDING = 0;
export const THUMBNAIL_PADDING_MODERN_VIEWER = 4;
export const THUMBNAIL_CONTAINER_BOTTOM_MARGIN = 7;
export const THUMBNAIL_CONTAINER_BOTTOM_MARGIN_MODERN_VIEWER = 24;
export const THUMBNAIL_BORDER_SIZE = 2;
export const THUMBNAIL_BORDER_SIZE_MODERN_VIEWER = 1;
export const THUMBNAIL_TOP_PADDING = 24;
export const THUMBNAIL_TOP_PADDING_MODERN_VIEWER = 0;
export const THUMBNAIL_BOTTOM_PADDING = 24;
export const THUMBNAIL_BORDER_COLOR_SELECTED_STATE = "var(--spectrum-global-color-blue-500, #1473E6)";
export const THUMBNAIL_BORDER_COLOR_UNSELECTED_STATE = "var(--spectrum-global-color-static-gray-400, #D3D3D3)";
export const THUMBNAIL_BACKGROUND_COLOR = "#e9e9e9";
export const THUMBAIL_BORDER_RADIUS_MODERN_VIEWER = 8;
export const THUMBNAIL_NUM_HEIGHT = 24;
export const THUMBNAIL_NUM_WIDTH = 20;
export const THUMBNAIL_NUM_TOP_MARGIN = 0;
export const THUMBNAIL_NUM_TOP_MARGIN_MODERN_VIEWER = 8;
export const THUMBNAIL_NUM_BOTTOM_MARGIN = 5;
export const THUMBNAIL_NUM_BOTTOM_MARGIN_MODERN_VIEWER = 24;
export const THUMBNAIL_NUM_HEIGHT_MODERN_VIEWER = 13;
export const THUMBNAIL_NUM_WIDTH_MODERN_VIEWER = 22;
export const DEFAULT_THUMBNAIL_ZOOM_LEVEL = 0.25;
export const PLUS_INDICATOR_SIZE = 150;

// Eureka
export const FIXED_MARGIN = 100;

export const HIDE_HUD_ICONS_THRESHOLD = 768;
export const HIDE_HUD_ICONS_MIN_THRESHOLD = 320;
export const PRESET_HIDE_HUD_ICONS_THRESHOLD = 320;
export const FILTER = "grayscale(100%) brightness(50%) sepia(109%) hue-rotate(-180deg) saturate(800%) contrast(0.6)";

export const MAIN_SCRIPT_NAME = "main.js";
export const DEFAULT_PAGE_HEIGHT = 842;
export const DEFAULT_PAGE_WIDTH = 595;
export const DEFAULT_PAGE_SIZE = 1200;
export const DEFAULT_PAGE_FORMAT = "jpg";
export const GRABBER_BG_COLOR = "#a4c6f4";
export const GRABBER_BORDER_RADIUS = "15px";

export const THUMBNAILS_VIEW_BACKGROUND_COLOR = "#F5F5F5";
export const THUMBNAILS_VIEW_BACKGROUND_COLOR_MODERN_VIEWER = "var(--spectrum-global-color-grey-50)";
export const TOOLTIP_LEFT_PLACEMENT = "left";
export const TOOLTIP_RIGHT_PLACEMENT = "right";
export const TOOLTIP_TOP_PLACEMENT = "top";
export const TOOLTIP_BOTTOM_PLACEMENT = "bottom";
export const TOOLTIP_DELAY = 1000;
export const COMMENTS_BACKGROUND_DIV_WIDTH = 325;

/**
 * React Virtualized Constants
 */
export const DOCUMENT_OVERSCAN_ROW_COUNT = 2;
export const THUMBNAILS_OVERSCAN_ROW_COUNT = 2;

export const AJS_CUSTOM_SCROLL_EVT = "ajs_custom_scroll_evt";

export const VIEWING_ORIGIN = "viewingOrigin";

export const AUTO_SCROLL_MARGIN = 16;
export const MAX_AUTO_SCROLL_SPEED = 16;
export const QUICK_TOOLS_MARGIN_TOP = 16;
export const QUICK_TOOLS_BORDER_RADIUS = 4;

// Tiling constants
export const TILE_OVERLAP_HEIGHT = 2; // in pixels
export const CACHE_PARTIAL_LOADING_TIME_DELTA = 1000;
export const ENABLE_TILING_DELTA = 750;

// Progressive rendering constants
export const PROGRESSIVE_RENDERING_INTERVAL = 50; // ms
export const PROGRESSIVE_RENDERING_INTERVAL_FIREFOX = 300; // ms
export const PROGRESSIVE_RENDERING_INTERVAL_LIMIT = 800; // ms
export const PROGRESSIVE_RENDERING_MULTIPLIER = 40000;

// FallbackTimout constant
export const FALLBACK_TIMEOUT = 5000; // 5sec

export const MAX_PAGE_RENDER_TIME = 500;
export const TIME_BETWEEN_PROGRESSIVE_BLITS = 500;

export const DCAPI_METADATA_MAX_TRIES = 20;
export const EXTERNAL_METADATA_MAX_TRIES = 20;

export const CONTEXT_MENU_MARGIN = 40;
export const CONTEXT_MENU_POSITION_DELTA = 6;
export const CONTEXT_MENU_MARGIN_COMMENTING = 20;

export const ANALYTICS_OUTLINE = "Summary";
export const ANALYTICS_ASSISTANT = "Assistant";
export const GENAI_OPERATION_PERFORM_EVENT_NAME = "genAIOperationPerformed";

export const reviewPathName = "/link/review";
export const trackPathName = "/link/track";
export const filePathName = "/link/file";
export const commentModePathName = "/link/file/comment";

export const VIEW_SDK_SCROLL = "viewsdkscroll";
export const VIEW_SDK_EVENT = "documentviewupdate";

export const COPY_TEXT_PERM = 4;
export const COMMENTING_PERM = 5;
export const PRINT_PERM = 2;
export const HIGH_QUALITY_PRINT_PERM = 11;
export const FORM_FILLING_PERM = 8;
export const DOCUMENT_ASSEMBLY_PERM = 10;
export const COPY_FOR_ACCESSIBILITY_PERM = 9;
export const MODIFIYING_CONTENT_PERM = 3;

export const EXTERNAL_TYPE = "/external";
export const SIGN_TYPE = "/sign";
export const CONTEXT = {
  INTEGRATION: "integration"
};

export const PASSWORD_PROTECTED_DOC_STRING = "Password protected document.";

export const IMAGE_APPEARANCE_DEFAULT_SCALE = 300 / 72.0;
export const GET_READER_URL = "https://get.adobe.com/reader/";

export const CONTEXT_MENU_SELECTION_NAME = "preview";

export const INTERACTIVE_ARIA_ROLES =
  'button:not([disabled]), input[type="text"]:not([disabled]), input[type="search"]:not([disabled]), [role="tab"], [role="switch"], [role="button"]';
export const HC_THEME_POLLING_INTERVAL = 1000; // 1 sec

export const MAX_LOG_FOR_PER_PAGE_RENDER_TIME = 10;

export const overlayEventNames = Object.freeze({
  TOOL_SELECTED: "toolselected",
  COMMENTS_PANEL_VISIBILITY_CHANGED: "commentsPanelVisibilityChanged",
  MULTI_DOC_MODE_CHANGED: "multiDocModeChanged",
  ORGANIZE_OP_PERFORMED: "organizeOperationPerformed"
});

export const LOCAL_LM_SERVICE_WORKER_SCOPE_PREFIX = `${window.location.origin}/dcpreviewdropin/2.35.2_1.1876.0`;
export const LM_SERVICE_WORKER_SCOPE_PREFIX = `${window.location.origin}/dcpreviewdropin/__VERSION__`;
export const ALL_QUICKTOOLS = "all";

export const EDIT_CLIENT_NAME = "edit";
export const CROP_CLIENT_NAME = "crop";
export const NUMBERPAGE_CLIENT_NAME = "numberPage";

export const MEGAVERB_ID_CONVERT = "group-convert";
export const ENABLE_PREMIUM_INDICATOR = "enable_premium_indicator";
export const ENABLE_TEXT_TO_IMAGE = "text_to_image_in_enabled";
export const PREMIUM_ICON_TOOLTIP_ID = "contextMenu.premiumIcon.tooltip";

export const SUBMIT_ACTION_DIALOG_STRINGS = Object.freeze({
  ERROR_TITLE: "dialog.submitAction.error.title",
  SUCCESS_TITLE: "dialog.submitAction.success.title",
  SUCCESS_TEXT: "dialog.submitAction.success.text",
  FAILURE_TEXT: "dialog.submitAction.failure.text",
  UNSUPPORTED_FORMAT_TEXT: "dialog.submitAction.unsupportedFormat.text",
  EMPTY_FIELD_TEXT: "dialog.submitAction.emptyField.text",
  UNSUPPORTED_PROTOCOL_TEXT: "dialog.submitAction.unsupportedProtocol.text"
});

export const RELOAD_ACCESSIBILITY_DEBOUNCE_WAIT = 5000;
export const SPEECHSYNTHESIS_BOUNDARY_TIMEOUT = 300;
export const SAFARI_FIREFOX_READALOUD_DEBOUNCE_WAIT = 300;
export const READALOUD_CACHE_WINDOW = 5;

export const JS_BROWSER_ALERT_SECURITY_HEADER = "js.dialog.browserAlert.security.header";
export const JS_BROWSER_ALERT_SECURITY_FOOTER = "js.dialog.browserAlert.security.footer";

export const BYTES_IN_MB = 1048576;

export const JS_DOC_TYPE = "Doc";
export const JS_PAGE_TYPE = "Page";
export const JS_MOUSE_UP_TYPE = "Mouse Up";

export const EDIT_DROPIN_TYPE = {
  NONE: 0,
  FULL: 1,
  PARTIAL: 2
};

export const LM_PERSONALISATION = Object.freeze({
  minimumFontSize: 50,
  maximumFontSize: 300,
  stepOfFontSize: 25,
  fontSizeUnit: "%",
  defaultFontSize: 100,

  minimumLineHeight: 1,
  maximumLineHeight: 2.5,
  stepOfLineHeight: 0.1,
  lineHeightUnit: "",
  defaultLineHeight: 1.2,

  minimumLetterSpacing: -0.1,
  maximumLetterSpacing: 0.15,
  stepOfLetterSpacing: 0.05,
  letterSpacingUnit: "px",
  defaultLetterSpacing: 0
});

export const NOTIFICATION_TYPE = Object.freeze({
  TOAST: "toast",
  BANNER: "banner"
});

export const NOTIFICATION_CLOSE_REASON = Object.freeze({
  TIMEOUT: "timeout",
  CLOSE_CLICK: "close_click",
  ADD_NEW: "add_new",
  API_CALL: "api_call",
  CLEAR_ALL: "clear_all"
});

export const FORM_ACCESSIBILITY_NODES_PREFIX = "form-access";

export const OVERLAY_VISIBILITY = Object.freeze({
  VISIBLE: "visible",
  HIDDEN: "hidden"
});

export const UPDATE_IN = Object.freeze({
  FORMS_MODULE: 1 << 0,
  DROPIN: 1 << 1,
  CORE: 1 << 2
});

export const darkModeFloodgateFlag = "dc-web-dark-mode";

export const YOUTUBE_REG_PATTERN = /^(https?\:\/\/)?((www\.)?youtube\.com((\/+)?)|youtu\.be(\/.+$))/;

export const CONTENT_DOCUMENT_NOT_SET = "contentDocumentNotSet";

export const FULL_SCREEN_MODE_BG_COLOR = "#000000";

export const CLOSE_RHP_BTN_ID = "close-rhp-button-id";

export const FILL_SIGN_OCG_NAME = "Adobe Fill & Sign";

export const PAGE_BY_PAGE_WINDOW_SIZE = 1;

// Linearization
export const LINEARIZATION_DEFAULT_BLOCK_SIZE = 65536;

export const SING_ASSET = "sign_asset";

export const PIXELS_PER_INCH = 72; // pdf defines 72 pixels per inch

export const UPSELL_VERBS_FOR_ANONYMOUS_USER = "UPSELL_VERBS_FOR_ANONYMOUS_USER";

export const EDIT_TEXT_RECIPIENT_FUNNEL = "DCWeb_edit_text_recipient_funnel";

export const KEYBOARD_SHORTCUTS = Object.freeze({
  ROTATE: "rotateView",
  ROTATE_ANTI_CLOCKWISE: "rotateViewAntiClockwise",
  GOTO_PAGE: "goToPage",
  FULL_SCREEN: "fullScreen",
  ZOOM_IN: "zoomIn",
  ZOOM_OUT: "zoomOut",
  RESET_ZOOM: "resetZoom",
  CANCEL_IMAGE_SELECTION: "cancelImageSelection"
});

export const PROGRESSIVE_RENDERING_WORKFLOWS = Object.freeze({
  PAGE_RENDERING: "page_rendering",
  EDIT_TYPING: "edit_typing"
});

export const GET_FEATURE_FLAG = "GET_FEATURE_FLAG";
export const GET_ASSET_VERSION = "GET_ASSET_VERSION";
export const WAIT_FOR_DOC_SAVE_COMPLETION = "WAIT_FOR_DOC_SAVE_COMPLETION";

export const FIREFOX_FULLSCREEN_SHORTCUT_TOAST_TIMEOUT = 5000;

export const GET_PAGE_RENDITION_LOG_TIMEOUT = 60 * 1000; // 1min

export const DIG_SIG_TYPE = Object.freeze({
  CERTIFICATION: "Certification",
  APPROVAL: "Approval",
  USAGE_RIGHTS: "UsageRights",
  DOCUMENT_TIMESTAMP: "DocumentTimestamp"
});

export const VALIDATION_STATUS = Object.freeze({
  VALID: "valid",
  INVALID: "invalid",
  UNKNOWN: "unknown",
  UNSUPPORTED: "unsupported"
});

export const CERTIFICATE_KEYS = Object.freeze({
  version: "Version",
  serialNumber: "Serial Number",
  issuer: "Issuer",
  subject: "Subject",
  validityStarts: "Validity Starts",
  validityEnds: "Validity Ends",
  keyUsage: "Key Usage",
  extendedKeyUsage: "Extended Key Usage",
  certificatePolicies: "Certificate Policies",
  crlDistributionPoints: "CRL Distribution Points",
  certificateAuthorityInformationAccess: "Certificate Authority Information Access",
  basicConstraints: "Basic Constraints",
  signatureAlgorithm: "Signature Algorithm",
  publicKey: "Public Key",
  subjectKeyIdentifier: "Subject Key Identifier",
  authorityKeyIdentifier: "Authority Key Identifier",
  x509Data: "X509 Data"
});

export const CERTIFICATE_POLICY_KEYS = Object.freeze({
  userNotice: "User Notice",
  certificatePolicyStatement: "Certificate Policy Statement"
});

export const DIGSIG_VALIDATOR_NAME = Object.freeze({
  AdobeSignValidator: "AdobeSignCertificateValidator",
  EdgeExtensionValidator: "EdgeExtensionCertificateValidator"
});

export const HTTP_ERROR_STRINGS = Object.freeze({
  tooManyRequests: "TooManyRequests",
  gatewayTimeout: "GatewayTimeout",
  internalServerError: "InternalServerError",
  apiTimeoutError: "APITimeoutError",
  unknownError: "UnknownError"
});

export const DIGSIG_VALIDATE_RETRY_TIMEOUT = 10 * 1000; // 10 seconds

export const LANDMARK_NAV_DIR = Object.freeze({
  FORWARD: 1,
  BACKWARD: 2
});

export const RICH_TEXT_DIV_ID = "formatted-copy-container";

export const LANDMARK_LIST = [
  "dc_one_tap_prompt_parent", // google-yolo
  "", // global-nav
  "tools-board-panel",
  "quick-tools-view",
  "pageview-current-page",
  "skinny-rail"
];

export const ACCESS_LAYER_SCALEX_THRESHOLD = 10;
export const ACCESS_LAYER_SCALEY_THRESHOLD = 10;

export const VIEWER_DOCUMENT_ID = "viewerDocumentId";

export const LOG_WAIT_TIMES_TIMEOUT = 10000; // 10sec

export const NEXT_MSG_WAIT_TIMEOUT = 10000; // 10sec

export const BULLET_POINT_CHARS = ["•", "●", "∙", "‣", "◦", "▸", "▪", "o"];

export const PERM_TOAST_CONSTANTS = Object.freeze({
  TOTAL_LIMIT: 3,
  LOCAL_STORAGE_KEY: "numTimesPermToastAppeared",
  TIMEOUT: 3000, // 3sec
  FIRST_TIMEOUT: 5000 // 5sec
});

export const GENAI_LEARN_MORE_LINK = "https://www.adobe.com/go/GenAIwebUserGuidelines";
export const GENAI_LEARN_MORE_LINK_INTERNAL =
  "https://helpx-internal.corp.adobe.com/content/help/en/acrobat/using/generative-ai-web.html#user-guidelines";

export const GENAI_USER_GUIDELINES_LINK = "https://www.adobe.com/go/GenAIwebUserGuidelines";
export const GENAI_USER_GUIDELINES_LINK_INTERNAL =
  "https://helpx-internal.corp.adobe.com/content/help/en/acrobat/using/generative-ai-web.html#user-guidelines";
export const GENAI_DATA_PROTECTION_LINK = "https://www.adobe.com/go/GenAIwebDataUsage";
export const GENAI_DATA_PROTECTION_LINK_INTERNAL =
  "https://helpx-internal.corp.adobe.com/content/help/en/acrobat/using/generative-ai-web.html#data-use";
export const GENAI_USER_GUIDELINES_LINK_FEEDBACK = "https://www.adobe.com/go/GenAIUserGuidelinesUserFeedbackWeb";

export const DEFAULT_RHP_WIDTH = 400;
export const SEARCH_INPUT_FIELD_FOCUS_BLUR_TIMEOUT = 300;
export const SEARCH_MENU_ITEMS_UPDATE_TIMEOUT = 300;
export const PAGES_TO_LOAD_IN_SCROLL_DIRECTION = 2;

export const MENU_BAR = "MenuBar";
export const RHP_OPEN_STATE_EVENT = "rhpOpenState";
export const SIDE_NAV_WIDTH_SESSION_STORAGE_KEY = "sid-nav-width";

export const GET_LOCAL_FONT_PRIMING_DIALOG = "GET_LOCAL_FONT_PRIMING_DIALOG";
export const SWITCH_PREVIEW_ANIMATION_DURATION = 200;

export const ORGANIZE_APIS = new Set([
  "organizeWillBegin",
  "insertPages",
  "insertBlankPage",
  "deletePages",
  "reorderPages",
  "rotatePages",
  "extractPages",
  "organizeDidEnd"
]);

export const PlusIndicatorVerticalHeight = 130;
export const PlusIndicatorHorizontalWidth = 170;

export const BOTTOM_PANEL_HIDE_TIMEOUT = 7000;
export const mobileCommentingTargetMboxId = "DCWeb_Collab_MOBILE_MV";
export const mobileCommentingTargetValue = "commentsEnabled";
export const SELECTIVELY_RENDER_ANNOTS_FLAG_FOR_MOBILE = 4294967295;
export const sharedFilesStickyTools = Object.freeze([
  "verb-comment-stickynote",
  "verb-comment-highlight",
  "verb-comment-strikeout",
  "verb-comment-draw",
  "verb-more-tools"
]);

export const HIDE_PANEL_STATES = Object.freeze({
  LHP: 1,
  RHP: 2,
  SCROLL: 3,
  SELECTION: 4,
  OVERLAP: 5,
  DISABLE: 6
});

export const BOTTOM_PANEL_MARGIN = 20;

export const IMAGE_SELECTION_PARAMS = {
  EXPERIMENT_FLAG: "DCWeb_edit_image_experiment",
  OPTIONS: {
    DISABLE_EDIT: "disable_edit_options",
    ENABLE_EDIT: "enable_edit_options",
    ENABLE_REPLACE: "enable_replace_options"
  }
};

export const EDIT_FRICTIONLESS_EXPERIMENT_TARGET_ID = "DCWeb_edit_panel_frictionless_flow";

export const TOP_OVERLAY_Z_INDEX = 200;

/*
  Chrome SharedArrayBuffer origin trial tokens corresponding to following urls

  localdev: https://local-test.acrobat.com:9000
  dev: https://dev.acrobat.adobe.com
  stage: https://stage.acrobat.adobe.com
  prod: https://acrobat.adobe.com
*/
export const CHROME_ORIGIN_TRIAL_EXPIRY_DATE = "2025-07-22";

export const SHARED_ARRAY_BUFFER_ORIGIN_TRIAL_TOKENS = Object.freeze({
  localdev:
    "Akm01mz5lKaGfyv48QfnsghoOy23JIT/SdOLL13QI7fXfmB+mk4cmps/110pE2n334xu4ZK+LZ6QF/0ZCtd7PgsAAACBeyJvcmlnaW4iOiJodHRwczovL2xvY2FsLXRlc3QuYWNyb2JhdC5jb206OTAwMCIsImZlYXR1cmUiOiJVbnJlc3RyaWN0ZWRTaGFyZWRBcnJheUJ1ZmZlciIsImV4cGlyeSI6MTc1MzE0MjQwMCwiaXNTdWJkb21haW4iOnRydWV9",
  dev:
    "AgLxvcN56Q9O6yX+pPd4vcCHCgT8Xm8OsBs8yU9FNR7bQOljYnRQsaE3APtYeEAbgj8QD8mGCIBr6iAa7Cq//QAAAAB/eyJvcmlnaW4iOiJodHRwczovL2Rldi5hY3JvYmF0LmFkb2JlLmNvbTo0NDMiLCJmZWF0dXJlIjoiVW5yZXN0cmljdGVkU2hhcmVkQXJyYXlCdWZmZXIiLCJleHBpcnkiOjE3NTMxNDI0MDAsImlzU3ViZG9tYWluIjp0cnVlfQ==",
  stage:
    "ArHfX6mtv5BvCRwdIR5dlLTEeVXFb4ZoUE8F2j0MFgRuuGDY/wK7/VAn4WaX17/5IXHf7igzY1Ta6r3gLbqwEwEAAACBeyJvcmlnaW4iOiJodHRwczovL3N0YWdlLmFjcm9iYXQuYWRvYmUuY29tOjQ0MyIsImZlYXR1cmUiOiJVbnJlc3RyaWN0ZWRTaGFyZWRBcnJheUJ1ZmZlciIsImV4cGlyeSI6MTc1MzE0MjQwMCwiaXNTdWJkb21haW4iOnRydWV9",
  prod:
    "AuEE7wFYMFWZxSUXIV89AqaRHjsotqYC5AbXR0nY4/9efaHRgn/5TeKR/p8uG9C2lLf92JsoxkSO/pD4ZdULGQsAAAB7eyJvcmlnaW4iOiJodHRwczovL2Fjcm9iYXQuYWRvYmUuY29tOjQ0MyIsImZlYXR1cmUiOiJVbnJlc3RyaWN0ZWRTaGFyZWRBcnJheUJ1ZmZlciIsImV4cGlyeSI6MTc1MzE0MjQwMCwiaXNTdWJkb21haW4iOnRydWV9"
});
